import { useCallback, useEffect, useState } from "react";
import { Trash2, UserPlus, ShieldCheck, Building2, Newspaper } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Input } from "@/components/ui/input";

const ROLE_META = {
  admin: { label: "Admin", icon: ShieldCheck, desc: "Full access — listings, news, inquiries and team management." },
  listings: { label: "Listings", icon: Building2, desc: "Can add, edit and publish property listings and see inquiries." },
  news: { label: "News", icon: Newspaper, desc: "Can publish and edit news articles only." },
};

const Label = ({ children }) => (
  <label className="block text-[11px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1.5">{children}</label>
);

export const TeamManager = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "news" });
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addUser = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/admin/users", form);
      toast.success(`${form.name} can now log in to the Staff Portal.`);
      setForm({ name: "", email: "", password: "", role: "news" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const changeRole = async (u, role) => {
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, { role });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? data : x)));
      toast.success(`${u.name} is now ${ROLE_META[role].label}.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.email}) from the Staff Portal?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Staff member removed.");
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="team-manager">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-foreground/10 border hairline">
        {Object.entries(ROLE_META).map(([key, { label, icon: Icon, desc }]) => (
          <div key={key} className="bg-white px-6 py-6">
            <Icon className="h-5 w-5 text-brand" />
            <div className="mt-3 font-display font-semibold">{label}</div>
            <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>

      <form onSubmit={addUser} className="mt-8 border hairline bg-white p-6" data-testid="team-add-form">
        <h3 className="font-display font-semibold">Invite a staff member</h3>
        <p className="mt-1 text-xs text-muted-foreground">They log in at the Staff Portal with this email and password.</p>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <Label>Name</Label>
            <Input data-testid="team-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" />
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="team-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none" />
          </div>
          <div>
            <Label>Password</Label>
            <Input data-testid="team-password-input" required type="text" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min. 8 characters" className="rounded-none" />
          </div>
          <div>
            <Label>Access</Label>
            <select data-testid="team-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-input bg-white px-3 py-2 text-sm h-9 outline-none">
              {Object.entries(ROLE_META).map(([key, { label }]) => <option key={key} value={key}>{label}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <button data-testid="team-add-button" disabled={adding} className="flex w-full items-center justify-center gap-2 bg-brand px-4 py-2.5 text-xs font-mono uppercase tracking-[0.15em] font-bold text-ivory hover:bg-brand-hover transition-colors disabled:opacity-50">
              <UserPlus className="h-4 w-4" /> {adding ? "Adding…" : "Add"}
            </button>
          </div>
        </div>
      </form>

      <div className="mt-6 border hairline bg-white overflow-x-auto" data-testid="team-table">
        <table className="w-full min-w-[680px] text-sm">
          <thead>
            <tr className="border-b hairline text-left text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
              <th className="px-5 py-4">Staff Member</th>
              <th className="px-5 py-4">Access</th>
              <th className="px-5 py-4">Added</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b hairline last:border-0" data-testid={`team-row-${u.id}`}>
                <td className="px-5 py-4">
                  <div className="font-medium">{u.name} {u.id === currentUser?.id && <span className="text-xs text-muted-foreground">(you)</span>}</div>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                </td>
                <td className="px-5 py-4">
                  <select
                    data-testid={`team-role-select-${u.id}`}
                    value={u.role}
                    disabled={u.id === currentUser?.id}
                    onChange={(e) => changeRole(u, e.target.value)}
                    className="border hairline bg-ivory px-2 py-1.5 text-xs outline-none disabled:opacity-50"
                  >
                    {Object.entries(ROLE_META).map(([key, { label }]) => <option key={key} value={key}>{label}</option>)}
                  </select>
                </td>
                <td className="px-5 py-4 text-xs text-muted-foreground whitespace-nowrap">{formatDate(u.created_at)}</td>
                <td className="px-5 py-4 text-right">
                  {u.id !== currentUser?.id && (
                    <button data-testid={`team-delete-${u.id}`} onClick={() => removeUser(u)} className="border hairline p-2 text-muted-foreground hover:text-red-600 hover:border-red-300 transition-colors" aria-label="Remove">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && users.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-12 text-center text-muted-foreground">No staff members yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
